/**
 * Dependencies.
 */
import { Component, Mixins, Watch } from 'vue-property-decorator';

import OrderSurveyModel from '@/api/delivery-survey/delivery-survey.model';
import SurveyMixin from '@/modules/survey/mixins/survey-mixin';

/**
 * Component.
 */
@Component({
  name: 'survey-step-1-scene'
})
export default class extends Mixins(SurveyMixin) {
  /**
   * Property `rating`.
   */
  rating = 0;

  /**
   * Property `selectedOrderSatisfaction`.
   */
  selectedOrderSatisfaction: string[] = [];

  /**
   * Property `comment`.
   */
  comment: string = null;

  /**
   * Property `survey`.
   */
  survey: OrderSurveyModel = null;

  /**
   * Getter `unhappyClient`.
   */
  get unhappyClient() {
    return this.rating > 0 && this.rating < 4;
  }

  /**
   * Getter `satisfiedClient`.
   */
  get satisfiedClient() {
    return this.rating === 4;
  }

  /**
   * Getter `amazedClient`.
   */
  get amazedClient() {
    return this.rating === 5;
  }

  /**
   * Getter `hasValidData`.
   */
  get hasValidData() {
    const hasComment = this.canShowComment;

    return (
      ((this.rating < 4 && this.selectedOrderSatisfaction.length > 0) ||
        (!!this.rating && this.rating < 4 && this.unhappy?.length === 0) ||
        this.rating >= 4) &&
      (!hasComment ||
        (hasComment &&
          this?.comment?.length > 0 &&
          this?.comment?.length <= 120))
    );
  }

  /**
   * Getter `canShowComment`.
   */
  get canShowComment() {
    return this.selectedOrderSatisfaction.includes('OTHER');
  }

  /**
   * Getter `clientName`.
   */
  get clientName() {
    const { delivery = null } = this.survey || {};
    const { client = null } = delivery || {};
    const { name = null } = client || {};

    return name;
  }

  /**
   * Getter `orderCode`.
   */
  get orderCode() {
    const { delivery } = this.survey || {};
    const { orderCode = null } = delivery || {};

    return orderCode;
  }

  /**
   * Getter `alreadySubmited`.
   */
  get alreadySubmited() {
    const { alreadySubmited } = this.survey || {};

    return alreadySubmited;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleInitialize();
  }

  /**
   * Handle initialize.
   */
  async handleInitialize() {
    this.isLoading = true;

    this.survey = await this.handleGetOrderSurvey();
    await this.handleGetTypes();

    this.isLoading = false;

    this.$emit('survey-order', { orderCode: this.orderCode });
  }

  /**
   * Handle select satisfaction.
   */
  handleSelectSatisfaction({ id }: { id: string }) {
    const isAlreadySelected = this.selectedOrderSatisfaction.includes(id);

    if (id === 'OTHER' && isAlreadySelected) {
      this.comment = null;
    }

    this.selectedOrderSatisfaction = !isAlreadySelected
      ? [...this.selectedOrderSatisfaction, id]
      : this.selectedOrderSatisfaction.filter(itemId => itemId !== id);
  }

  /**
   * Handle next step.
   */
  handleNextStep() {
    const { key } = this.$route?.params || {};
    const { comment, rating, selectedOrderSatisfaction } = this || {};

    this.$router.push({
      name: 'SurveyStep2',
      params: {
        commentsTypes: JSON.stringify(this.commentsTypes),
        entityCommentsTypes: JSON.stringify(this.entityCommentsTypes),
        key,
        orderComment: comment,
        orderRating: String(rating),
        orderSurvey: JSON.stringify(this.survey),
        selectedOrderSatisfaction: String(selectedOrderSatisfaction)
      }
    });
  }

  /**
   * Handle rating change.
   */
  @Watch('rating')
  handleRatingChange(newRating: number, oldRating: number) {
    const oldRatingLevel = this.handleGetClientRatingLevel({
      rating: oldRating
    });
    const newRatingLevel = this.handleGetClientRatingLevel({
      rating: newRating
    });

    if (newRatingLevel !== oldRatingLevel) {
      this.selectedOrderSatisfaction = [];
      this.comment = null;
    }
  }
}
